import { E_CompanyArea } from '@/apis/service/CompanyApi.types';
import { country_en, country_cn } from '@/@core/utils/variables';
import { E_Currency } from '../../apis/service/BoCommon.types';
import { SUPERSET_BASE_Features, SUPERSET_CN_Features, SUPERSET_EU_Features, SUPERSET_US_Features } from './supersetFeatures';
/**
 * 基础功能表：类似数据库建表的思想
 */
const BASE_Features = {
    // 访问美国时候提示访问欧洲版本
    VISIT_EU_TIP: false,
    // 支持 InnoUpsell 功能
    ENABLE_INNO_UPSELL: false,
    // 支持访问课程管理链接
    // ref: https://patsnap.atlassian.net/browse/OPP-1461
    ENABLE_COURSE_MANAGEMENT: false,
    // 导航库功能
    ENABLE_MARKETPLACE: false,
    // 智慧芽运营管理
    ENABLE_PATSNAP_OPERATION: true,
    /**
     * 是否显示合同号
     */
    ENABLE_CONTRACT: false,
    ENABLE_ACCOUNT_ACTIVE: true,
    /**
     * 是否支持privilege上的并发
     */
    ENABLE_PRIVILEGE_CONCURRENCY: true,
    /**
     * 使用nickname / firstname+lastname
     */
    USE_NICKNAME: false,
    // ref: https://confluence.zhihuiya.com/pages/viewpage.action?pageId=114941203
    /**
     * 强制使用货币
     */
    FORCE_USE_CURRENCY: false,
    /**
     * 默认货币
     */
    DEFAULT_CURRENCY: E_Currency.USD,
    /**
     * 区域选择
     */
    AREAS_OPTIONS: [],
    /**
     * 默认区域
     */
    DEFAULT_AREA: null,
    /**
     * 国家选择
     */
    COUNTRIES: country_en,
    // 创建试用时候是否禁止选择Area
    // ref: https://confluence.zhihuiya.com/pages/viewpage.action?pageId=112031559
    ENABLE_SELECT_AREA_ON_ADD_TRAIL: true,
    // 使用销售易?SALESFORCE?
    USE_SALESFORCE: false,
    USE_XIAOSHOUYI: false,
    /*
     * 编辑用户是否可以编辑手机号
     */
    USE_EDIT_MOBILE: false,
    // #region 限制相关
    /**
     * tip: 限制相关的也放在features里面, 防止每个国家不一样!!
     */
    /**
     * 邮箱限制
     */
    LIMIT_EMAILS: 100,
    /**
     * 每日每个并发导出专利数码
     */
    LIMIT_PRE_DAY_CONCURRENCY_EXPORT_NUMBER: 20000,
    // #endregion 限制相关
    /**
     * 使用记录导出
     */
    USE_EXPORT_USAGE_DATA: true,
    /**
     * 邮件响应区分国内外：
     */
    USE_MAIL_RECEIPT: true,
    /**
     * 是否支持图表报告
     */
    ENABLE_CHART_REPORT: true,
    /**
     * 是否支持PhoneNumber
     */
    ENABLE_PHONE_NUMBER: false,
    /**
    * 是否支持课程管理
    */
    ENABLE_MANAGEMENT_COURSE: false,
    /**
    * 是否支持资产管理
    */
    ENABLE_INNOSNAP: false,
    /**
     * 自注册地图展示
     */
    ENABLE_CHINESEMAP: false,
    /**
     * Pendo SSO 入口
     */
    ENABLE_PENDO: false,
    /**
     * 是否允许专利数限制功能
     */
    ENABLE_PATENT_NUMBER_LIMIT: false,
    /**
     * 显示北森数据
     */
    ENABLE_BEISEN: false,
    /**
     * 角色控制
     */
    ROLES: {},
    /**
     * SUPERSET控制
     */
    ENABLE_SUPERSET: true,
    /**
     *     ENABLE_SUPERSET_NEW_FEATURE控制
     */
    ENABLE_SUPERSET_NEW_FEATURE: false,
    /**
     * weiChat 二维码
     */
    ENABLE_WEI_CHAT: false,
    /**
     * 公司认证标识 二维码
     */
    ENABLE_COMPANY_LEGITIMATE: false,
    /**
      * 看版 - sales列表,
      * 是否只能看到自己的cliente ，true 则 sales 列表中只能看到自己
      */
    ENABLE_DASHBOARD_SALES_VIEW_OWN_CLIENTE: false,
    /**
      * 创建自注册链接
      * 新 - 线索来源 （市场活动模块中获取）
      * 隐藏旧的 渠道来源，渠道编码，项目活动
      */
    PROMOTION_LINK_NEW_LEAD_SOURCE: false,
    /**
      * 开启公司名称的合法验证
      * */
    ENABLE_COMPANY_NAME_VALIDATED_LEGITIMATE: false,
    /** 是否开启看板功能 */
    ENABLE_DASHBOARD: true,
    /**  运营推广 从xiaoshouyi同步 */
    ENABLE_SYNC_PROMOTION_XIAOSHOUYI: false,
    /** 账号解锁，功能解锁 开启钉钉审批确认 */
    ENABLE_DINGTALK_APPROVAL: false,
    /** 自注册链接二维码图片名称 */
    COMPANY_REGISTER_IMAGE_NAME: 'register_cn_qr.png',
    /**
     * 是否放开openAPI功能
     */
    ENABLE_OPEN_API: true,
    /**
     * 是否放开lab功能
     */
    ENABLE_LAB: true,
    /**
     * 是否放开Prompt管理
     */
    ENABLE_PROMPT_MANAGEMENT: true,
    /**
     * 是否放开邮件模板管理
     */
    ENABLE_EMAIL_TEMPLATE_MANAGEMENT: true,
    ...SUPERSET_BASE_Features,
    /**
     * 组织/公司每日导出上限
     */
    COMPANY_DAILY_EXPORT_LIMIT: null,
    /**
     * 放开AB test
     */
    ENABLE_ABTEST: true,
};
/**
 * 中国功能表
 *  1.记录中国环境具备的功能
 */
const CN_Features = {
    ...BASE_Features,
    ENABLE_INNO_UPSELL: true,
    ENABLE_COURSE_MANAGEMENT: true,
    ENABLE_MARKETPLACE: true,
    ENABLE_ACCOUNT_ACTIVE: true,
    ENABLE_CONTRACT: true,
    USE_NICKNAME: true,
    FORCE_USE_CURRENCY: true,
    DEFAULT_CURRENCY: E_Currency.CNY,
    AREAS_OPTIONS: [E_CompanyArea.east, E_CompanyArea.west],
    DEFAULT_AREA: E_CompanyArea.east,
    COUNTRIES: country_cn,
    ENABLE_SELECT_AREA_ON_ADD_TRAIL: false,
    USE_XIAOSHOUYI: true,
    USE_EDIT_MOBILE: true,
    USE_MAIL_RECEIPT: true,
    ENABLE_PHONE_NUMBER: true,
    ENABLE_MANAGEMENT_COURSE: true,
    ENABLE_INNOSNAP: true,
    ENABLE_CHINESEMAP: true,
    ENABLE_PENDO: false,
    ENABLE_BEISEN: true,
    ENABLE_COMPANY_NAME_VALIDATED_LEGITIMATE: true,
    ENABLE_SUPERSET_NEW_FEATURE: true,
    ROLES: {
        id_backoffice_sales: [
            'id_backoffice_only_view_own_customer',
            'maintain_trial_permission'
        ],
        id_backoffice_am_ka: [
            'id_backoffice_only_view_own_customer',
            'maintain_trial_permission'
        ],
        id_backoffice_cs: [
            'account_management_security',
            'edit_org_security_setting',
            'id_backoffice_maintain_contact',
            'maintain_trial_permission'
        ],
        id_backoffice_pm: [
            'data_protection',
            'id_backoffice_maintain_contact',
            'maintain_trial_permission',
            'operation_manager'
        ],
        backoffice_resource_viewer: [
            'id_backoffice_only_view_own_customer'
        ],
    },
    ENABLE_WEI_CHAT: true,
    ENABLE_COMPANY_LEGITIMATE: true,
    ENABLE_DASHBOARD_SALES_VIEW_OWN_CLIENTE: false,
    PROMOTION_LINK_NEW_LEAD_SOURCE: true,
    ENABLE_SYNC_PROMOTION_XIAOSHOUYI: true,
    ENABLE_DINGTALK_APPROVAL: false,
    COMPANY_REGISTER_IMAGE_NAME: 'register_cn_qr.png',
    ...SUPERSET_CN_Features,
    COMPANY_DAILY_EXPORT_LIMIT: 5000000,
};
const US_Features = {
    ...BASE_Features,
    AREAS_OPTIONS: [E_CompanyArea.east, E_CompanyArea.west],
    DEFAULT_AREA: E_CompanyArea.west,
    USE_SALESFORCE: true,
    ENABLE_DASHBOARD_SALES_VIEW_OWN_CLIENTE: true,
    PROMOTION_LINK_NEW_LEAD_SOURCE: false,
    ROLES: {
        id_backoffice_cs: [
            'account_management_security',
            'edit_org_security_setting',
            'id_backoffice_maintain_contact',
            'maintain_trial_permission'
        ],
        id_backoffice_pm: [
            'data_protection',
            'id_backoffice_maintain_contact',
            'account_management_security',
            'edit_org_security_setting',
            'maintain_trial_permission',
            'operation_manager'
        ],
    },
    COMPANY_REGISTER_IMAGE_NAME: 'register_us_qr.png',
    ENABLE_LAB: false,
    ENABLE_PROMPT_MANAGEMENT: false,
    ENABLE_EMAIL_TEMPLATE_MANAGEMENT: false,
    ...SUPERSET_US_Features,
};
// 继承US_Features
const EU_Features = {
    ...US_Features,
    VISIT_EU_TIP: true,
    ENABLE_DASHBOARD: false,
    ENABLE_SUPERSET: true,
    ROLES: {
        id_backoffice_cs: [
            'account_management_security',
            'edit_org_security_setting',
            'id_backoffice_maintain_contact',
            'maintain_trial_permission'
        ],
        id_backoffice_pm: [
            'data_protection',
            'id_backoffice_maintain_contact',
            'id_backoffice_maintain_cooperative_order',
            'maintain_trial_permission',
            'operation_manager'
        ],
    },
    COMPANY_REGISTER_IMAGE_NAME: 'register_eu_qr.png',
    USE_EXPORT_USAGE_DATA: false,
    ENABLE_OPEN_API: false,
    ENABLE_LAB: false,
    ENABLE_PROMPT_MANAGEMENT: false,
    ENABLE_EMAIL_TEMPLATE_MANAGEMENT: false,
    ...SUPERSET_EU_Features,
    ENABLE_ABTEST: false,
};
export { BASE_Features, CN_Features, US_Features, EU_Features, };
